import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoSmartPoint, SmartPointCategory } from '../../IFikoThreeJS/FikoSmartPoint';
import * as THREE from 'three';

/**
 * Smart Point 2D Dialog - CSS2D Renderer versiyonu
 * Z-index ile derinlik sıralaması (klasik)
 */
@Component({
    selector: 'app-fiko-smart-point-2d',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './fiko-smart-point-2d.component.html',
    styleUrls: ['./fiko-smart-point-2d.component.css']
})
export class FikoSmartPoint2DComponent implements OnInit, OnDestroy {
    @Input() anaGosterim: FikoThreeJsComponent;
    @Input() pencere: any;

    // Temel ayarlar
    baslik: string = 'Nokta';
    aciklama: string = '';
    kategori: SmartPointCategory = 'info';
    etiketGorunsun: boolean = false;
    sabitBoyut: boolean = true;
    pulseAktif: boolean = false;

    // Durum
    noktaPozisyonu: THREE.Vector3 | null = null;
    secimModu: boolean = false;
    private mevcutNokta: FikoSmartPoint | null = null;

    // Kategoriler
    kategoriler: { value: SmartPointCategory; label: string; ikon: string; renk: string }[] = [
        { value: 'info', label: 'Bilgi', ikon: 'ℹ️', renk: '#2196F3' },
        { value: 'warning', label: 'Uyarı', ikon: '⚠️', renk: '#FF9800' },
        { value: 'error', label: 'Hata', ikon: '❌', renk: '#F44336' },
        { value: 'success', label: 'Onay', ikon: '✓', renk: '#4CAF50' },
        { value: 'measure', label: 'Ölçüm', ikon: '📏', renk: '#9C27B0' },
        { value: 'reference', label: 'Referans', ikon: '📍', renk: '#00BCD4' },
        { value: 'custom', label: 'Özel', ikon: '●', renk: '#607D8B' }
    ];

    constructor(
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) {}

    get eklemeAktif(): boolean {
        return this.noktaPozisyonu !== null && this.baslik.trim().length > 0;
    }

    get secilenKategori() {
        return this.kategoriler.find(k => k.value === this.kategori);
    }

    ngOnInit(): void {}

    ngOnDestroy(): void {
        if (this.secimModu) {
            this.secimModunuKapat();
        }
        this.onizlemeyiTemizle();
    }

    secKategori(kat: SmartPointCategory): void {
        this.kategori = kat;
        this.onizlemeGuncelle();
    }

    pozisyonSec(): void {
        this.secimModu = true;
        const canvas = this.anaGosterim?.sunucu?.domElement;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
            canvas.addEventListener('click', this.noktaTiklama);
        }
    }

    private noktaTiklama = (event: MouseEvent) => {
        const position = this.get3DPositionFromMouse(event);
        if (!position) return;

        this.ngZone.run(() => {
            this.noktaPozisyonu = position;
            this.secimModunuKapat();
            this.onizlemeGuncelle();
            this.cdr.detectChanges();
        });
    };

    private secimModunuKapat(): void {
        this.secimModu = false;
        const canvas = this.anaGosterim?.sunucu?.domElement;
        if (canvas) {
            canvas.style.cursor = 'default';
            canvas.removeEventListener('click', this.noktaTiklama);
        }
    }

    private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
        if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

        const canvas = this.anaGosterim.sunucu.domElement;
        const rect = canvas.getBoundingClientRect();

        const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

        const sahne = this.anaGosterim.sahneler?.[0];
        if (!sahne) return null;

        const meshes: THREE.Mesh[] = [];
        sahne.traverse((obj: THREE.Object3D) => {
            if (obj instanceof THREE.Mesh && obj.geometry && obj.visible) {
                if (!obj.name.includes('Helper') && !obj.name.includes('SmartPoint')) {
                    meshes.push(obj);
                }
            }
        });

        const intersects = raycaster.intersectObjects(meshes, true);
        if (intersects.length > 0) {
            return intersects[0].point.clone();
        }

        // Fallback - plane intersection
        const cameraDir = new THREE.Vector3();
        this.anaGosterim.aktifKamera.getWorldDirection(cameraDir);
        const plane = new THREE.Plane(cameraDir.negate(), this.anaGosterim.aktifKamera.position.length() * 0.5);
        const intersection = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(plane, intersection)) {
            return intersection;
        }
        return null;
    }

    onizlemeGuncelle(): void {
        if (!this.noktaPozisyonu) return;

        const sahne = this.anaGosterim?.sahneler?.[0];
        if (!sahne) return;

        this.onizlemeyiTemizle();

        this.mevcutNokta = new FikoSmartPoint({
            baslik: this.baslik,
            aciklama: this.aciklama,
            kategori: this.kategori,
            etiketGorunsun: this.etiketGorunsun,
            sabitBoyut: this.sabitBoyut,
            pulseAktif: this.pulseAktif
        });

        this.mevcutNokta.init();
        this.mevcutNokta.setPosition(this.noktaPozisyonu);
        this.mevcutNokta.hazirla(sahne);

        this.anaGosterim?.ciz();
    }

    private onizlemeyiTemizle(): void {
        if (this.mevcutNokta) {
            this.mevcutNokta.dispose();
            this.mevcutNokta = null;
        }
    }

    ekle(): void {
        if (!this.noktaPozisyonu || !this.mevcutNokta) return;

        const sahne = this.anaGosterim?.sahneler?.[0];
        if (sahne) {
            // Metadata olarak sakla
            if (!sahne.userData['smartPoints']) {
                sahne.userData['smartPoints'] = [];
            }
            sahne.userData['smartPoints'].push(this.mevcutNokta);
        }

        this.anaGosterim?.ciz();
        this.anaGosterim?.FikoYT?.degisti?.emit();

        // Reset
        this.mevcutNokta = null;
        this.noktaPozisyonu = null;
        this.baslik = 'Nokta';
        this.aciklama = '';
        this.cdr.detectChanges();
    }

    kapat(): void {
        this.onizlemeyiTemizle();
        const dialogInstance = this.pencere?.instance ?? this.pencere;
        if (dialogInstance?.kapa) {
            dialogInstance.kapa();
        } else if (dialogInstance?.kapat) {
            dialogInstance.kapat();
        }
    }
}
